// Connectivity Monitor Service
// Tracks online/offline status and provides event handling for network changes

class ConnectivityMonitor {
    constructor() {
        this.isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
        this.listeners = [];
        this.offlineStartTime = null;
        this.offlineDuration = 0;
        this._tickInterval = null;

        // Periodic health-probe state.
        // The browser `online`/`offline` events only fire on OS-level network changes
        // (e.g. WiFi disconnect), NOT when the LAN backend at 192.168.1.26:3500
        // becomes unreachable while the LAN itself is still up. Without this probe,
        // a single failed ping leaves `isOnline = false` stuck until the user
        // changes screens (which remounts subscribers and triggers `forceCheck`).
        this._healthProbeTimeoutId = null;
        this._probeInFlight = false;
        this._consecutiveFailures = 0;
        this._lastProbeTime = 0;
        this._baseProbeIntervalMs = 15000;   // 15s on a healthy LAN is responsive without being aggressive
        this._maxProbeIntervalMs = 60000;    // Cap backoff at 60s to bound recovery time
        this._visibilityChangeHandler = null;
        this._beforeUnloadHandler = null;
        this._pageHideHandler = null;

        // Restore persisted offline start time if browser reports offline
        try {
            const persisted = localStorage.getItem('offlineStartTime');
            if (!this.isOnline) {
                if (persisted) {
                    const ts = parseInt(persisted, 10);
                    if (!isNaN(ts)) {
                        this.offlineStartTime = ts;
                    }
                }
                // If no persisted time, set now as start
                if (!this.offlineStartTime) {
                    this.offlineStartTime = Date.now();
                    try { localStorage.setItem('offlineStartTime', String(this.offlineStartTime)); } catch (e) {}
                }
            }
        } catch (e) {
            // ignore storage errors
        }

        this._initEventListeners();
        if (!this.isOnline) {
            this._startTickTimer();
        }

        // Start the background probe so a sticky-offline state self-heals
        // once the backend is reachable again. Registered exactly once, here.
        this._initHealthProbeLifecycle();
    }

    _initEventListeners() {
        // On `online`, reset the probe backoff and fire an immediate probe —
        // the OS just told us the link is back, so verify the backend
        // is actually reachable without waiting up to 15s.
        window.addEventListener('online', () => {
            this._resetProbeBackoff();
            this._scheduleNextProbe(0);
        });
        // On `offline`, do NOT stop the probe — let it confirm the state
        // and re-discover when the backend is reachable again. `_handleOffline`
        // only manages the UI tick timer.
        window.addEventListener('offline', () => this._handleOffline());
    }

    /**
     * Wire up listeners that control the probe lifecycle (visibility, page hide/unload).
     * Registered exactly once in the constructor; torn down in `dispose()`.
     */
    _initHealthProbeLifecycle() {
        if (typeof document === 'undefined' || typeof window === 'undefined') return;

        // When the tab becomes visible again, fire a probe only if the last
        // one is older than the base interval. This avoids double-firing when
        // the probe already ran recently (e.g. via the `online` handler).
        this._visibilityChangeHandler = () => {
            if (typeof document.hidden === 'boolean' && document.hidden) return;
            const sinceLast = Date.now() - (this._lastProbeTime || 0);
            if (sinceLast >= this._baseProbeIntervalMs) {
                this._scheduleNextProbe(0);
            }
        };
        document.addEventListener('visibilitychange', this._visibilityChangeHandler);

        // Clean up timers when the window/page goes away so we don't leave
        // dangling intervals if the monitor is recreated (HMR, route remount, etc.).
        this._beforeUnloadHandler = () => this._stopHealthProbe();
        this._pageHideHandler = () => this._stopHealthProbe();
        window.addEventListener('beforeunload', this._beforeUnloadHandler);
        window.addEventListener('pagehide', this._pageHideHandler);

        // Kick off the first probe after the base interval.
        this._scheduleNextProbe(this._baseProbeIntervalMs);
    }

    _handleOnline() {
        const wasOffline = !this.isOnline;
        this.isOnline = true;
        
        if (wasOffline && this.offlineStartTime) {
            this.offlineDuration = Date.now() - this.offlineStartTime;
            this.offlineStartTime = null;
        }
        try {
            localStorage.removeItem('offlineStartTime');
        } catch (e) {}
        this._stopTickTimer();
        
        this._notifyListeners('online', {
            wasOffline,
            offlineDuration: this.offlineDuration
        });
    }

    _handleOffline() {
        this.isOnline = false;
        this.offlineStartTime = Date.now();
        try {
            localStorage.setItem('offlineStartTime', String(this.offlineStartTime));
        } catch (e) {}
        this._startTickTimer();
        
        this._notifyListeners('offline', {
            timestamp: this.offlineStartTime
        });
    }

    _notifyListeners(event, data) {
        this.listeners.forEach(listener => {
            if (listener.event === event || listener.event === 'all') {
                try {
                    listener.callback(data);
                } catch (error) {
                    console.error('Error in connectivity listener:', error);
                }
            }
        });
    }

    _startTickTimer() {
        if (this._tickInterval) return;
        this._tickInterval = setInterval(() => {
            if (!this.isOnline && this.offlineStartTime) {
                // emit tick event once per second while offline
                this._notifyListeners('tick', { now: Date.now() });
            }
        }, 1000);
    }

    _stopTickTimer() {
        if (this._tickInterval) {
            clearInterval(this._tickInterval);
            this._tickInterval = null;
        }
    }

    /**
     * Subscribe to connectivity events
     * @param {String} event - 'online', 'offline', or 'all'
     * @param {Function} callback - Callback function to invoke on event
     * @returns {Function} - Unsubscribe function
     */
    subscribe(event, callback) {
        const listener = { event, callback };
        this.listeners.push(listener);
        
        // Return unsubscribe function
        return () => {
            const index = this.listeners.indexOf(listener);
            if (index > -1) {
                this.listeners.splice(index, 1);
            }
        };
    }

    /**
     * Check current online status
     * @returns {Boolean} - True if online, false otherwise
     */
    getStatus() {
        return this.isOnline;
    }

    /**
     * Explicitly set online status (used by native/platform bridges)
     * @param {boolean} isOnline
     */
    setOnline(isOnline) {
        if (typeof isOnline !== 'boolean' || isOnline === this.isOnline) return;
        if (isOnline) {
            this._handleOnline();
        } else {
            this._handleOffline();
        }
    }

    /**
     * Get offline duration in milliseconds
     * @returns {Number} - Milliseconds offline (0 if online)
     */
    getOfflineDuration() {
        if (this.isOnline) {
            return 0;
        }
        if (this.offlineStartTime) {
            return Date.now() - this.offlineStartTime;
        }
        return 0;
    }

    /**
     * Get offline duration formatted as human-readable string
     * @returns {String} - Formatted duration (e.g., "2h 30m")
     */
    getOfflineDurationFormatted() {
        const duration = this.getOfflineDuration();
        if (duration === 0) {
            return 'Online';
        }
        
        const seconds = Math.floor(duration / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const remainingMinutes = minutes % 60;
        const remainingSeconds = seconds % 60;
        
        if (hours > 0) {
            return `${hours}h ${remainingMinutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m ${remainingSeconds}s`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Check if offline duration exceeds threshold
     * @param {Number} thresholdMs - Threshold in milliseconds
     * @returns {Boolean} - True if offline duration exceeds threshold
     */
    isOfflineLongerThan(thresholdMs) {
        return this.getOfflineDuration() > thresholdMs;
    }

    /**
     * Get remaining time before offline limit (3 hours)
     * @param {Number} limitMs - Limit in milliseconds (default 3 hours)
     * @returns {Number} - Remaining milliseconds (0 if online or limit exceeded)
     */
    getRemainingOfflineTime(limitMs = 3 * 60 * 60 * 1000) {
        if (this.isOnline) {
            return limitMs;
        }
        const duration = this.getOfflineDuration();
        const remaining = limitMs - duration;
        return Math.max(0, remaining);
    }

    /**
     * Get remaining time formatted
     * @param {Number} limitMs - Limit in milliseconds (default 3 hours)
     * @returns {String} - Formatted remaining time
     */
    getRemainingOfflineTimeFormatted(limitMs = 3 * 60 * 60 * 1000) {
        const remaining = this.getRemainingOfflineTime(limitMs);
        
        if (remaining === 0) {
            return 'Limit reached';
        }
        
        const seconds = Math.floor(remaining / 1000);
        const minutes = Math.floor(seconds / 60);
        const hours = Math.floor(minutes / 60);
        
        const remainingMinutes = minutes % 60;
        
        if (hours > 0) {
            return `${hours}h ${remainingMinutes}m`;
        } else if (minutes > 0) {
            return `${minutes}m`;
        } else {
            return `${seconds}s`;
        }
    }

    /**
     * Test connectivity by attempting a lightweight network request
     * @returns {Promise<Boolean>} - True if request succeeds
     */
    async testConnectivity() {
        // Online/offline is defined by backend reachability.
        const tryFetch = async (url, options = {}) => {
            const controller = new AbortController();
            const timeout = options.timeout || 4000;
            const id = setTimeout(() => controller.abort(), timeout);
            try {
                const res = await fetch(url, {
                    method: options.method || 'GET',
                    cache: 'no-cache',
                    mode: options.mode || 'no-cors', // no-cors allows opaque pass-through when CORS is strict
                    signal: controller.signal,
                });
                clearTimeout(id);
                // In no-cors mode, response.ok is false for opaque responses; treat any resolution as reachable
                return true;
            } catch (e) {
                clearTimeout(id);
                return false;
            }
        };

        // Try configured backend check endpoint first.
        if (typeof window !== 'undefined' && window.serverApi) {
            const checkUrl = this._buildBackendUrl('print/check-threads');
            return await tryFetch(checkUrl, { timeout: 4000 });
        }

        // If backend URL is unavailable, fallback to browser network state.
        return typeof navigator !== 'undefined' ? navigator.onLine : false;
    }

    /**
     * Fail-fast connectivity check for invoicing.
     * Performs one quick request to the backend health endpoint and avoids extra probes.
     * @param {Object} options
     * @param {Number} options.pingTimeoutMs - Timeout in ms (default: 10000)
     * @param {Number} options.minSpeedKbps - Minimum acceptable probe throughput in kbps (default: 5)
     * @param {Number} options.minSampleBytes - Minimum payload size to apply kbps gate (default: 512)
     * @param {Number} options.smallPayloadMaxPingMs - Max allowed latency for tiny payloads (default: 1500)
     * @param {String} options.path - Backend path to probe (default: print/check-threads)
     * @returns {Promise<{poor: boolean, reason: string|null, metrics: Object}>}
     */
    async assessConnectionQuality(options = {}) {
        const config = {
            pingTimeoutMs: Number(options.pingTimeoutMs || 10000),
            minSpeedKbps: Number(options.minSpeedKbps || 5),
            minSampleBytes: Number(options.minSampleBytes || 512),
            smallPayloadMaxPingMs: Number(options.smallPayloadMaxPingMs || 1500),
            path: String(options.path || 'print/check-threads'),
        };

        const metrics = {
            endpoint: null,
            pingMs: null,
            bytes: null,
            speedKbps: null,
            payloadBytes: null,
            contentLengthBytes: null,
            transferBytes: null,
        };

        const targetUrl = this._buildBackendUrl(config.path);
        metrics.endpoint = targetUrl;
        const pingResult = await this._pingEndpoint(targetUrl, config.pingTimeoutMs);
        metrics.pingMs = pingResult.ms;
        metrics.bytes = pingResult.bytes;
        metrics.speedKbps = pingResult.speedKbps;
        metrics.payloadBytes = pingResult.payloadBytes;
        metrics.contentLengthBytes = pingResult.contentLengthBytes;
        metrics.transferBytes = pingResult.transferBytes;

        if (!pingResult.ok) {
            return { poor: true, reason: 'probe_failed', metrics };
        }

        if (Number.isFinite(metrics.bytes) && metrics.bytes > 0 && metrics.bytes < config.minSampleBytes) {
            const poorByLatency = Number.isFinite(metrics.pingMs) && metrics.pingMs > config.smallPayloadMaxPingMs;
            if (poorByLatency) {
                return { poor: true, reason: 'latency_above_threshold_for_small_payload', metrics };
            }

            return { poor: false, reason: null, metrics };
        }

        if (Number.isFinite(metrics.speedKbps) && metrics.speedKbps < config.minSpeedKbps) {
            return { poor: true, reason: 'speed_below_threshold', metrics };
        }

        return { poor: false, reason: null, metrics };
    }

    _buildBackendUrl(path = '') {
        if (!(typeof window !== 'undefined' && window.serverApi)) return '';
        const base = String(window.serverApi || '').replace(/\/+$/, '');
        const suffix = String(path || '').replace(/^\/+/, '');
        return suffix ? `${base}/${suffix}` : base;
    }

    async _pingEndpoint(url, timeoutMs = 10000) {
        const start = Date.now();
        try {
            if (!url) {
                return { ok: false, ms: Date.now() - start, bytes: 0, speedKbps: 0 };
            }
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), timeoutMs);
            try {
                const perfStart = typeof performance !== 'undefined' && performance.now ? performance.now() : null;
                const response = await fetch(url, {
                    method: 'GET',
                    cache: 'no-cache',
                    signal: controller.signal,
                });
                let payloadBytes = 0;
                try {
                    const bodyText = await response.text();
                    payloadBytes = new TextEncoder().encode(bodyText || '').length;
                } catch (_) {
                    // Keep payloadBytes as 0 and rely on fallback sources below.
                }

                let bytes = payloadBytes;
                const contentLength = Number(response?.headers?.get?.('content-length') || 0);
                const contentLengthBytes = Number.isFinite(contentLength) && contentLength > 0
                    ? Math.trunc(contentLength)
                    : 0;
                if (contentLengthBytes > 0) {
                    bytes = Math.max(bytes, contentLengthBytes);
                }

                const transferBytes = this._getResourceTransferBytes(response?.url || url, perfStart);
                if (Number.isFinite(transferBytes) && transferBytes > 0) {
                    bytes = Math.max(bytes, Math.trunc(transferBytes));
                }

                const elapsedMs = Date.now() - start;
                const speedKbps = elapsedMs > 0
                    ? ((bytes * 8) / (elapsedMs / 1000)) / 1000
                    : 0;
                return {
                    ok: true,
                    ms: elapsedMs,
                    bytes,
                    speedKbps: Number.isFinite(speedKbps) ? Number(speedKbps.toFixed(3)) : 0,
                    payloadBytes,
                    contentLengthBytes,
                    transferBytes: Number.isFinite(transferBytes) && transferBytes > 0 ? Math.trunc(transferBytes) : 0,
                    status: response?.status || 0,
                };
            } finally {
                clearTimeout(id);
            }
        } catch (e) {
            return { ok: false, ms: Date.now() - start, bytes: 0, speedKbps: 0 };
        }
    }

    _getResourceTransferBytes(url, perfStart = null) {
        try {
            if (!url || typeof performance === 'undefined' || !performance.getEntriesByName) return 0;
            const entries = performance.getEntriesByName(url);
            if (!Array.isArray(entries) || entries.length === 0) return 0;
            const recent = entries.filter(entry =>
                typeof entry?.transferSize === 'number' &&
                entry.transferSize >= 0 &&
                (perfStart == null || (typeof entry.startTime === 'number' && entry.startTime >= perfStart - 25))
            );
            if (recent.length === 0) return 0;
            const candidate = recent[recent.length - 1];
            const transferSize = Number(candidate.transferSize || 0);
            const encodedBodySize = Number(candidate.encodedBodySize || 0);
            const decodedBodySize = Number(candidate.decodedBodySize || 0);
            return Math.max(transferSize, encodedBodySize, decodedBodySize, 0);
        } catch (_) {
            return 0;
        }
    }

    async _pingBackend(timeoutMs = 5000) {
        const start = Date.now();
        try {
            if (!(typeof window !== 'undefined' && window.serverApi)) {
                return { ok: !!(typeof navigator !== 'undefined' ? navigator.onLine : false), ms: Date.now() - start };
            }
            const checkUrl = this._buildBackendUrl('print/check-threads');
            if (!checkUrl) {
                return { ok: false, ms: Date.now() - start };
            }
            const controller = new AbortController();
            const id = setTimeout(() => controller.abort(), timeoutMs);
            try {
                await fetch(checkUrl, {
                    method: 'GET',
                    cache: 'no-cache',
                    mode: 'no-cors',
                    signal: controller.signal,
                });
                return { ok: true, ms: Date.now() - start };
            } finally {
                clearTimeout(id);
            }
        } catch (e) {
            return { ok: false, ms: Date.now() - start };
        }
    }

    /**
     * Compute the next probe delay based on the current consecutive-failure count.
     * Exponential backoff: 15s -> 30s -> 60s, then capped at 60s.
     * Resets to base on the first successful probe.
     * @returns {Number} Delay in milliseconds before the next probe.
     */
    _getNextProbeDelay() {
        const base = this._baseProbeIntervalMs;
        const cap = this._maxProbeIntervalMs;
        const failures = Math.max(0, this._consecutiveFailures | 0);
        if (failures === 0) return base;
        // 1 failure -> 1x base, 2 failures -> 2x base, 3+ -> 4x base, capped.
        const multiplier = Math.min(1 << Math.min(failures - 1, 4), Math.floor(cap / base) || 1);
        return Math.min(cap, base * multiplier);
    }

    /**
     * Reset the probe backoff to the base interval. Called on browser `online`
     * events and after a successful probe.
     */
    _resetProbeBackoff() {
        this._consecutiveFailures = 0;
    }

    /**
     * Schedule the next probe after `delayMs`. Cancels any previously scheduled probe.
     * If `delayMs === 0` and a probe is already in flight, the call is a no-op.
     * @param {Number} delayMs - Milliseconds to wait before running the next probe.
     */
    _scheduleNextProbe(delayMs) {
        if (typeof window === 'undefined') return;
        if (this._healthProbeTimeoutId != null) {
            clearTimeout(this._healthProbeTimeoutId);
            this._healthProbeTimeoutId = null;
        }
        const wait = Math.max(0, Number(delayMs) || 0);
        this._healthProbeTimeoutId = setTimeout(() => {
            this._healthProbeTimeoutId = null;
            // Skip the scheduled tick while the tab is hidden — the
            // visibilitychange listener will fire an immediate probe on return.
            if (typeof document !== 'undefined' && typeof document.hidden === 'boolean' && document.hidden) {
                // Reschedule for the same delay so we don't lose the cadence.
                this._scheduleNextProbe(this._getNextProbeDelay());
                return;
            }
            this._runHealthProbe();
        }, wait);
    }

    /**
     * Execute one health probe against the configured backend, update state,
     * and schedule the next probe with appropriate backoff. The in-flight guard
     * prevents overlapping requests from slow or stuck probes.
     */
    async _runHealthProbe() {
        if (this._probeInFlight) return; // overlap guard
        this._probeInFlight = true;
        this._lastProbeTime = Date.now();
        try {
            const result = await this._pingBackend(5000);
            if (result && result.ok) {
                // Success: reset backoff. If we were considered offline, promote to online
                // so the existing listener contract (NavigationDrawer, etc.) is preserved.
                this._resetProbeBackoff();
                if (!this.isOnline) {
                    this._handleOnline();
                }
            } else {
                // Failure: increment backoff. Only flip state to offline if we currently
                // believe we are online — we don't want to double-fire offline events while
                // already in the offline state.
                this._consecutiveFailures = (this._consecutiveFailures | 0) + 1;
                if (this.isOnline) {
                    this._handleOffline();
                }
            }
        } catch (_) {
            this._consecutiveFailures = (this._consecutiveFailures | 0) + 1;
            if (this.isOnline) {
                this._handleOffline();
            }
        } finally {
            this._probeInFlight = false;
            // Always schedule the next probe — do NOT skip when isOnline === false,
            // because that is exactly the condition we are trying to self-heal.
            this._scheduleNextProbe(this._getNextProbeDelay());
        }
    }

    /**
     * Stop the probe. Called from `beforeunload` / `pagehide` and from `dispose()`.
     * Idempotent.
     */
    _stopHealthProbe() {
        if (this._healthProbeTimeoutId != null && typeof window !== 'undefined') {
            clearTimeout(this._healthProbeTimeoutId);
            this._healthProbeTimeoutId = null;
        }
    }

    /**
     * Defensive cleanup. Removes the listeners registered in the constructor
     * and stops the probe. Safe to call multiple times. Currently no caller invokes
     * this (the singleton lives for the app lifetime), but it is provided so the
     * listener contract is symmetrical and HMR / SPA remounts can recover cleanly.
     */
    dispose() {
        this._stopHealthProbe();
        if (typeof document !== 'undefined' && this._visibilityChangeHandler) {
            try { document.removeEventListener('visibilitychange', this._visibilityChangeHandler); } catch (_) {}
            this._visibilityChangeHandler = null;
        }
        if (typeof window !== 'undefined') {
            if (this._beforeUnloadHandler) {
                try { window.removeEventListener('beforeunload', this._beforeUnloadHandler); } catch (_) {}
                this._beforeUnloadHandler = null;
            }
            if (this._pageHideHandler) {
                try { window.removeEventListener('pagehide', this._pageHideHandler); } catch (_) {}
                this._pageHideHandler = null;
            }
        }
        this.listeners = [];
    }

    /**
     * Force check and update online status
     * @returns {Promise<Boolean>} - Updated online status
     */
    async forceCheck() {
        const actuallyOnline = await this.testConnectivity();
        
        if (actuallyOnline !== this.isOnline) {
            if (actuallyOnline) {
                this._handleOnline();
            } else {
                this._handleOffline();
            }
        }
        
        return actuallyOnline;
    }
}

// Create singleton instance
const connectivityMonitor = new ConnectivityMonitor();

export default connectivityMonitor;
